import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  Heading,
  Text,
  useToast,
  Flex,
  Container,
  FormHelperText,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RESET_PASSWORD_URL } from "../services/api";
import { useAuth } from "../context/AuthContext";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const toast = useToast();
  const { login } = useAuth();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token) {
      toast({
        title: "Invalid Link",
        description: "Password reset link is invalid or missing.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      navigate("/login");
    }
  }, [token, navigate, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      return toast({
        title: "Missing Fields",
        description: "Please enter and confirm your new password.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (newPassword !== confirmPassword) {
      return toast({
        title: "Password Mismatch",
        description: "Passwords do not match.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    if (newPassword.length < 6) {
      return toast({
        title: "Weak Password",
        description: "Password must be at least 6 characters long.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsLoading(true);
    try {
      const response = await fetch(RESET_PASSWORD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Password Reset Successful",
          description: "Logging you in...",
          status: "success",
          duration: 2000,
          isClosable: true,
        });

        // Auto-login if email is available
        if (data.email) {
          // Wait a moment for the toast to show
          setTimeout(async () => {
            const loginResult = await login(data.email, newPassword);
            if (loginResult.success) {
              navigate("/");
            } else {
              // If auto-login fails, redirect to login page
              toast({
                title: "Login Required",
                description: "Please login with your new password.",
                status: "info",
                duration: 3000,
              });
              navigate("/login");
            }
          }, 1000);
        } else {
          // Redirect to login page if email not available
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        }
      } else {
        throw new Error(data.message || "Failed to reset password");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message || "Failed to reset password. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  return (
    <Flex
      width="100vw"
      height="100vh"
      bg="gray.50"
      align="center"
      justify="center"
      p={4}
    >
      <Container maxW="md">
        <Box bg="white" borderRadius="xl" p={8} boxShadow="xl">
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <VStack spacing={2} align="flex-start">
              <Heading fontSize="3xl" color="gray.800" fontWeight="700">
                Reset Password
              </Heading>
              <Text fontSize="md" color="gray.600">
                Enter your new password below
              </Text>
            </VStack>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <VStack spacing={5}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                    New Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter new password"
                      size="lg"
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: "gray.300", bg: "white" }}
                      _focus={{
                        bg: "white",
                        borderColor: "#0B4885",
                        boxShadow: "0 0 0 1px #0B4885",
                      }}
                      borderRadius="lg"
                      autoFocus
                    />
                    <InputRightElement width="3rem">
                      <IconButton
                        h="1.75rem"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormHelperText color="gray.600" fontSize="xs">
                    Password must be at least 6 characters long
                  </FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                    Confirm Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Confirm new password"
                      size="lg"
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: "gray.300", bg: "white" }}
                      _focus={{
                        bg: "white",
                        borderColor: "#0B4885",
                        boxShadow: "0 0 0 1px #0B4885",
                      }}
                      borderRadius="lg"
                    />
                    <InputRightElement width="3rem">
                      <IconButton
                        h="1.75rem"
                        size="sm"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        icon={
                          showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                        }
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  bg="#0B4885"
                  color="white"
                  size="lg"
                  width="100%"
                  type="submit"
                  isLoading={isLoading}
                  loadingText="Resetting..."
                  fontSize="md"
                  fontWeight="600"
                  _hover={{
                    bg: "#0a3d6e",
                    transform: "translateY(-1px)",
                    boxShadow: "lg",
                  }}
                  _active={{
                    bg: "#083156",
                    transform: "translateY(0)",
                  }}
                  mt={2}
                  borderRadius="lg"
                  transition="all 0.2s"
                  boxShadow="md"
                >
                  Reset Password
                </Button>

                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => navigate("/login")}
                  width="100%"
                  color="gray.600"
                  _hover={{ bg: "gray.100" }}
                >
                  Back to Login
                </Button>
              </VStack>
            </form>
          </VStack>
        </Box>
      </Container>
    </Flex>
  );
};

export default ResetPasswordPage;
